// AddAdsModal.tsx
import { useState, useEffect } from 'react';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { Select, DatePicker, Input, Upload, Button } from 'antd';
import { ImCancelCircle } from 'react-icons/im';
import { InboxOutlined, DeleteOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import { BeatLoader } from "react-spinners";
import dayjs from 'dayjs';
import './add-ads-modal.css';
import { createAds, updateAds } from '../../../services/ads';
import { ref, uploadBytes, getDownloadURL, deleteObject, getStorage } from 'firebase/storage';
import { firebaseApp } from '../../../utils/firebaseConfig';

const { Option } = Select;
const { Dragger } = Upload;
const { RangePicker } = DatePicker;

const validationSchema = Yup.object().shape({
  institutionId: Yup.string().required('Institution is required'),
  institutionName: Yup.string().required('Institution name is required'),
  status: Yup.string().required('Status is required'),
  startDate: Yup.date().required('Start date is required'),
  endDate: Yup.date().required('End date is required')
    .min(Yup.ref('startDate'), 'End date must be after start date'),
});

const AddAdsModal = ({ onClose, onContentAdded, editData = null, institutions = [] } : any) => {
    const [loading, setLoading] = useState(false);
    const [mediaFiles, setMediaFiles] = useState<any[]>([]);
    const storage = getStorage(firebaseApp);
  
    const isEditMode = !!editData;
  
    const initialValues = {
        institutionId: editData?.institutionId || undefined,
        institutionName: editData?.institutionName || '',
        startDate: editData?.startDate?._seconds ? 
            dayjs(new Date(editData.startDate._seconds * 1000)) : 
        null,
        endDate: editData?.endDate?._seconds ? 
            dayjs(new Date(editData.endDate._seconds * 1000)) : 
        null,
        status:  editData?.status || ''
    };

    useEffect(() => {
      if (isEditMode && editData.mediaFiles) {
        setMediaFiles(editData.mediaFiles);
      }
    }, [editData]);
  
    const uploadToFirebase = async (file: File) => {
      try {
        const fileRef = ref(storage, `ads/${Date.now()}-${file.name}`);
        await uploadBytes(fileRef, file);
        const downloadURL = await getDownloadURL(fileRef);
        return downloadURL;
      } catch (error) {
        throw error;
      }
    };
  
    const deleteFromFirebase = async (fileUrl: string) => {
      try {
        const fileRef = ref(storage, fileUrl);
        await deleteObject(fileRef);
      } catch (error) {
        console.error('Error deleting file:', error);
      }
    };
  
    const uploadProps = {
      accept: '.jpg,.jpeg,.png,.mp4,.mov',
      multiple: true,
      beforeUpload: async (file: File) => {
        const isValidType = [
          'image/jpeg',
          'image/png',
          'video/mp4',
          'video/quicktime'
        ].includes(file.type);
  
        if (!isValidType) {
          toast.error('Invalid file type');
          return Upload.LIST_IGNORE;
        }
  
        const isValidSize = file.size / 1024 / 1024 < 50; // 50MB limit
        if (!isValidSize) {
          toast.error('File must be smaller than 50MB');
          return Upload.LIST_IGNORE;
        }
  
        const type = file.type.includes('video') ? 'video' : 'image';
        
        try {
          setLoading(true);
          const url = await uploadToFirebase(file);
          setMediaFiles(prev => [...prev, { url, type }]);
        } catch (error) {
          toast.error('Error uploading file');
          console.error('Upload error:', error);
        } finally {
          setLoading(false);
        }
        
        return false;
      },
      showUploadList: false
    };
  
    const handleMediaDelete = async (fileUrl: string) => {
      try {
        await deleteFromFirebase(fileUrl);
        setMediaFiles(prev => prev.filter(file => file.url !== fileUrl));
        toast.success('File deleted successfully');
      } catch (error) {
        toast.error('Error deleting file');
      }
    };
  
    const handleSubmit = async (values: any) => {
      try {
        setLoading(true);
  
        if (mediaFiles.length === 0) {
          toast.error('At least one media file is required');
          return;
        }
  
        const adData = {
          ...values,
          mediaFiles,
          startDate: values.startDate.toDate(),
          endDate: values.endDate.toDate()
        };
  
        if (isEditMode) {
          await updateAds(editData.id, adData);
          toast.success('Advertisement updated successfully');
        } else {
          await createAds(adData);
          toast.success('Advertisement created successfully');
        }
  
        onContentAdded();
        onClose();
      } catch (error) {
        // Clean up uploaded files if request fails
        mediaFiles.forEach(async (file) => {
          if (!editData?.mediaFiles?.find((f: any) => f.url === file.url)) {
            await deleteFromFirebase(file.url);
          }
        });
        toast.error(isEditMode ? 'Error updating advertisement' : 'Error creating advertisement');
      } finally {
        setLoading(false);
      }
    };

  // Function to filter options based on input
  const filterOption = (input: string, option: any) => {
    return option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0;
  };

  return (
    <div className="modal-wrapper">
      <div className="modal-container">
        <div className="modal-header">
          <h2>{isEditMode ? 'Edit Advertisement' : 'Add New Advertisement'}</h2>
          <ImCancelCircle 
            className="close-icon"
            onClick={onClose} 
            size={22} 
          />
        </div>

        <div className="modal-content">
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ values, errors, touched, handleChange, setFieldValue } : any) => (
              <Form className="form-container">
                <div className="form-group">
                  <label>Institution</label>
                  <Select
                    showSearch
                    placeholder="Institution"
                    className="custom-select"
                    optionFilterProp="children"
                    filterOption={filterOption}
                    onChange={(value, option: any) => {
                      setFieldValue('institutionId', value);
                      setFieldValue('institutionName', option.children);
                    }}
                    value={values.institutionId}
                  >
                    {institutions.map((inst: any) => (
                      <Option key={inst.id} value={inst.id}>
                        {inst.facilityName}
                      </Option>
                    ))}
                  </Select>
                  {errors.institutionId && touched.institutionId && (
                    <div className="error-message">{errors.institutionId}</div>
                  )}
                </div>

                <div className="form-group">
                  <label>Media Files</label>
                  <Dragger {...uploadProps} className="media-uploader">
                    <p className="ant-upload-drag-icon">
                      <InboxOutlined />
                    </p>
                    <p className="ant-upload-text">Click or drag files to upload</p>
                    <p className="ant-upload-hint">
                      Support for images and videos up to 50MB
                    </p>
                  </Dragger>

                  <div className="media-preview-container">
                    {mediaFiles.map((file, index) => (
                      <div key={index} className="media-preview">
                        {file.type === 'image' ? (
                          <img src={file.url} alt="Preview" />
                        ) : (
                          <video src={file.url} controls />
                        )}
                        <Button
                          icon={<DeleteOutlined />}
                          onClick={() => handleMediaDelete(file.url)}
                          className="delete-button"
                        />
                      </div>
                    ))}
                  </div>
                </div>

                <div className="form-group">
                  <label>Campaign Duration</label>
                  <RangePicker
                    className="custom-rangepicker"
                    onChange={(dates) => {
                      if (dates) {
                        setFieldValue('startDate', dayjs(dates[0]));
                        setFieldValue('endDate', dayjs(dates[1]));
                      } else {
                        setFieldValue('startDate', null);
                        setFieldValue('endDate', null);
                      }
                    }}
                    value={[
                        values.startDate ? dayjs(values.startDate) : null,
                        values.endDate ? dayjs(values.endDate) : null
                      ]}
                  />
                  {(errors.startDate || errors.endDate) && (
                    <div className="error-message">{errors.startDate || errors.endDate}</div>
                  )}
                </div>

                <div className="form-group">
                  <label>Status</label>
                  <Select
                    className="custom-select"
                    onChange={(value) => setFieldValue('status', value)}
                    value={values.status}
                  >
                    <Option value="active">Active</Option>
                    <Option value="disabled">Disabled</Option>
                  </Select>
                  {errors.status && touched.status && (
                    <div className="error-message">{errors.status}</div>
                  )}
                </div>

                <div className="button-container">
                  {loading ? (
                    <BeatLoader color="#3080ED" />
                  ) : (
                    <button type="submit" className="submit-button">
                      {isEditMode ? 'Update Advertisement' : 'Create Advertisement'}
                    </button>
                  )}
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
      <div className="modal-overlay" onClick={onClose} />
    </div>
  );
};

export default AddAdsModal;